<template>
  <div>
    <!-- Dialog de Detalhes (lista de CIDs do paciente) — oculto no modo openDirectAdd -->
    <v-dialog
      v-if="!openDirectAdd"
      v-model="internalValue"
      transition="dialog-bottom-transition"
      max-width="1000px"
      scrollable
    >
      <v-card class="ds-dialog">
        <v-card-title
          class="ds-dialog-header d-flex justify-space-between align-center"
        >
          <div class="d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-clipboard-list-outline</v-icon>
            <span class="ds-dialog-title">{{ dialogTitle }}</span>
          </div>
          <div class="d-flex align-center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  color="white"
                  @click="openAddDialog"
                  class="mr-1"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </template>
              <span>Adicionar doença</span>
            </v-tooltip>
            <v-btn
              icon
              color="white"
              class="close-btn"
              @click="closeDetailsDialog"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text class="ds-dialog-body">
          <section v-if="cids.length" class="ds-form-section ds-form-section--flush">
            <v-simple-table class="history-table">
              <thead>
                <tr>
                  <th class="text-left text-uppercase caption font-weight-bold pa-4" style="width: 110px">
                    Código
                  </th>
                  <th class="text-left text-uppercase caption font-weight-bold pa-4">
                    Descrição
                  </th>
                  <th class="text-left text-uppercase caption font-weight-bold pa-4" style="width: 160px">
                    Data
                  </th>
                  <th class="text-left text-uppercase caption font-weight-bold pa-4" style="width: 90px">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in cids" :key="item.id" class="history-row">
                  <td class="pa-4">
                    <v-chip x-small label color="#ECFEFF" text-color="catTeal" class="font-weight-bold">
                      {{ item.code }}
                    </v-chip>
                  </td>
                  <td class="pa-4">
                    <span class="body-2 font-weight-medium">{{ item.description }}</span>
                  </td>
                  <td class="text-no-wrap pa-4">
                    <div class="d-flex align-center">
                      <v-icon small color="grey" class="mr-2">mdi-calendar</v-icon>
                      <span class="body-2">{{ formatDate(item.created_at) }}</span>
                    </div>
                  </td>
                  <td class="pa-4">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          x-small
                          color="primary"
                          class="action-btn mr-1"
                          @click="openLinker(item)"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon small>mdi-link-variant</v-icon>
                        </v-btn>
                      </template>
                      <span>Vincular</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          x-small
                          color="error"
                          class="action-btn"
                          @click="confirmDelete(item)"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon small>mdi-trash-can-outline</v-icon>
                        </v-btn>
                      </template>
                      <span>Excluir</span>
                    </v-tooltip>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </section>

          <!-- Estado vazio -->
          <section v-else class="ds-form-section text-center py-8 px-4">
            <v-icon size="56" color="grey lighten-1">mdi-clipboard-text-outline</v-icon>
            <p class="body-1 grey--text mt-3 mb-0">Nenhuma doença registrada.</p>
            <v-btn color="catTeal" outlined small class="mt-4" @click="openAddDialog">
              <v-icon small left>mdi-plus</v-icon>
              Adicionar primeiro CID
            </v-btn>
          </section>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Dialog de Adicionar -->
    <v-dialog v-model="formDialogVisible" max-width="600px" scrollable>
      <v-card class="ds-dialog">
        <v-card-title
          class="ds-dialog-header d-flex justify-space-between align-center"
        >
          <div class="d-flex align-center" style="position: relative; z-index: 1">
            <v-icon color="white" class="mr-2">mdi-plus-circle-outline</v-icon>
            <span class="ds-dialog-title">Adicionar doença</span>
          </div>
          <v-btn icon color="white" class="close-btn" @click="closeFormDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="ds-dialog-body">
          <section class="ds-form-section">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-clipboard-pulse-outline</v-icon>
                <span>Doenças</span>
              </div>
            </div>
            <CidField :key="'cid-' + formRenderKey" v-model="selectedCids" />
          </section>
        </v-card-text>

        <v-card-actions class="ds-dialog-actions">
          <v-btn outlined color="primary" @click="closeFormDialog">
            <v-icon left>mdi-close</v-icon>
            Cancelar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="catTeal"
            dark
            :disabled="!canSave"
            :loading="formLoading"
            depressed
            class="px-6"
            @click="saveData"
          >
            <v-icon small left>mdi-plus</v-icon>
            Adicionar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Vincular condição ↔ qualquer coisa do paciente (correlação universal) -->
    <CorrelationLinker
      v-model="linkerOpen"
      source-type="condition"
      :source-id="linkerCid ? linkerCid.id : null"
      :source-label="linkerCid ? linkerCid.description : ''"
      :patient-id="patientId"
    />
  </div>
</template>

<script>
import { useSnackbar } from "@/composables/useSnackbar";
import { createConditions, deleteCondition } from "@/services/conditionService";
import CidField from "@/features/patients/profile-dashboard/fields/CidField.vue";
import CorrelationLinker from "@/features/patients/profile-dashboard/shared/CorrelationLinker.vue";
import { formatDateTime } from "@/utils/datetime";

export default {
  name: "EditCidsDialog",

  components: {
    CidField,
    CorrelationLinker,
  },

  props: {
    value: { type: Boolean, default: false },
    dialogTitle: { type: String, default: "CIDs do paciente" },
    cids: { type: Array, default: () => [] },
    patientId: { type: [String, Number], required: true },
    // Quando true, abre direto o form de adicionar (pula o dialog de lista).
    openDirectAdd: { type: Boolean, default: false },
  },

  setup() {
    const { success, error, warning, erroDeRequisicao } = useSnackbar();
    return { success, error, warning, erroDeRequisicao };
  },

  data() {
    return {
      formDialogVisible: false,
      selectedCids: [],
      formLoading: false,
      deleteLoading: false,
      itemToDelete: null,
      // Bumped a cada abertura — força o remount do CidField (limpa busca).
      formRenderKey: 0,
      linkerOpen: false,
      linkerCid: null,
    };
  },

  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },

    canSave() {
      return !this.formLoading && this.selectedCids.length > 0;
    },
  },

  watch: {
    value(val) {
      if (val && this.openDirectAdd) {
        this.$nextTick(() => this.openAddDialog());
      }
    },
    formDialogVisible(val) {
      // No modo openDirectAdd, fechar o form fecha o dialog inteiro.
      if (!val && this.openDirectAdd) {
        this.$emit("input", false);
      }
    },
  },

  methods: {
    formatDate: formatDateTime,
    closeDetailsDialog() {
      this.$emit("input", false);
    },

    openLinker(cid) {
      this.linkerCid = cid;
      this.linkerOpen = true;
    },

    openAddDialog() {
      this.selectedCids = [];
      this.formRenderKey += 1;
      this.formDialogVisible = true;
    },

    closeFormDialog() {
      this.formDialogVisible = false;
      this.selectedCids = [];
    },

    async saveData() {
      if (this.selectedCids.length === 0) {
        this.warning("Adicione ao menos um CID");
        return;
      }
      this.formLoading = true;
      try {
        // Grava na tabela unificada patient_conditions com a categoria escolhida
        // por item (cid geral / crônica / transtorno psicológico).
        const conditions = this.selectedCids.map((c) => ({
          code: c.code,
          description: c.description,
          description_abbrev: c.description_abbrev || null,
          parent_code: c.parent_code || null,
          snomed_code: c.snomed_code || null,
          tuss_code: c.tuss_code || null,
          category: c.category || "cid",
        }));
        await createConditions(this.patientId, conditions);
        // salvia-781: guarda antes de fechar o form, que limpa a selecao.
        const registrado = this.selectedCids;
        this.success("CID(s) adicionado(s) com sucesso!");
        this.closeFormDialog();
        this.$emit("data-saved", registrado);
      } catch (err) {
        console.error("Erro ao salvar CIDs:", err);
        this.erroDeRequisicao(err, err.response?.data?.message || "Erro ao salvar os CIDs");
      } finally {
        this.formLoading = false;
      }
    },

    async confirmDelete(item) {
      this.itemToDelete = item;
      const ok = await this.$confirm({
        type: "delete",
        title: "Confirmar exclusão",
        message: `Tem certeza que deseja remover o CID ${item.code}?`,
        hint: "Esta ação não poderá ser desfeita.",
      });
      if (!ok) {
        this.itemToDelete = null;
        return;
      }
      await this.deleteData();
    },

    async deleteData() {
      if (!this.itemToDelete) return;
      this.deleteLoading = true;
      try {
        await deleteCondition(this.itemToDelete.id);
        this.success("CID removido com sucesso!");
        this.itemToDelete = null;
        this.$emit("data-deleted");
      } catch (err) {
        console.error("Erro ao excluir CID:", err);
        this.erroDeRequisicao(err, err.response?.data?.message || "Erro ao remover o CID");
      } finally {
        this.deleteLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ═══ Design system dialog (tema Ciano / Teal — CIDs) ═══ */
.ds-dialog {
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(31, 77, 63, 0.18),
    0 4px 12px rgba(0, 0, 0, 0.06) !important;
}

.ds-dialog-header {
  background: linear-gradient(135deg, var(--ds-primary) 0%, var(--ds-primary-deep) 100%);
  color: var(--ds-white);
}

.ds-dialog-title {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.3px;
}

.ds-dialog-header :deep(.v-btn) {
  position: relative;
  z-index: 1;
}

.ds-dialog-header :deep(.v-btn:hover) {
  background-color: rgba(255, 255, 255, 0.15) !important;
}

/* ═══ Tabela ═══ */
.history-table thead th {
  background-color: var(--ds-teal-bg) !important;
  border-bottom: 2px solid var(--ds-primary-border) !important;
  letter-spacing: 0.4px;
  color: var(--ds-teal) !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
}

.history-row {
  transition: background-color 0.15s ease;
}

.history-row:hover {
  background-color: var(--ds-teal-bg) !important;
}

.history-row td {
  border-bottom: 1px solid #e0f7fa !important;
  color: var(--ds-text-600);
}

.action-btn {
  transition: transform 0.15s ease, background-color 0.15s ease;
}

.action-btn:hover {
  transform: scale(1.15);
}
</style>
